import { RPost, RPostBody } from '../remote/post';
import { Post } from '../domain/post';
import { LinkToUpload, UploadedMedia, UploadType } from '../../Upload';
import { mapUser } from '../../User';
import { parseISO } from 'date-fns';
import { mapRFiles, mapRLinks, userToAuthor } from '../../../api/helpers';
import { isLinkToUpload, removeEmptyValues } from '../../../utils';
import { mapProject } from '../../Project';

export type RawPost = {
    relation: { projectId?: string };
    userId: string;
    title: string;
    text: string;
    linksToUpload: LinkToUpload[];
};

export type PostUpdate = {
    title: string;
    text: string;
    linksToUpload: (LinkToUpload | UploadedMedia<UploadType.Weblink>)[];
};
/**
 * Mapper to create a post.
 * @param takes all data from a post and
 * @returns a body that can be send to STRAPI.
 */
export const mapPostDataToBody = ({
    relation,
    userId,
    title,
    text,
    linksToUpload
}: RawPost): RPostBody => {
    const { projectId } = relation;
    const links = linksToUpload.map(({ data, description, source }) => ({
        url: data,
        description: description ?? null,
        source: source ?? null
    }));

    return {
        title,
        text,
        project: projectId ? { set: [projectId] } : undefined,
        author: { set: [userId] },
        links
    };
};

/**
 * Mapper to update a post.
 * Omits the fields project and author, bc these values cannot be changed
 * @returns the body to update a post.
 */
export const mapPostDataToUpdateBody = ({
    title,
    text,
    linksToUpload
}: PostUpdate): Omit<RPostBody, 'project' | 'author'> => {
    const links = linksToUpload?.map((link) =>
        isLinkToUpload(link)
            ? {
                  url: link.data,
                  description: link.description ?? null,
                  source: link.source ?? null
              }
            : {
                  url: link.url,
                  description: link.description ?? null,
                  source: link.source ?? null
              }
    );

    return { title, text, links };
};

const mapPost = (post: RPost): Post | undefined => {
    const { documentId, title, text, author, project, files, links, ...timeStampAttributes } = post;
    const { createdAt, updatedAt, publishedAt } = timeStampAttributes;

    const mappedAuthor = author ? userToAuthor(mapUser(author)) : undefined;
    const mappedProject = project ? mapProject(project) : undefined;
    if (mappedProject === null) {
        console.log(
            `Could not map Post with id${post.id}. Could not map a belonging project or group.`
        );
        return;
    }

    return {
        id: documentId,
        title,
        text,
        uploads: [...mapRFiles(files), ...mapRLinks(links)],
        project: mappedProject ?? undefined,
        author: mappedAuthor,
        createdAt: parseISO(createdAt),
        updatedAt: parseISO(updatedAt),
        publishedAt: publishedAt ? parseISO(publishedAt) : null
    };
};

export const mapPosts = (posts?: RPost[]) => (posts ? removeEmptyValues(posts.map(mapPost)) : []);
